//@flow
import * as React from 'react';
import { type TFunction } from 'react-i18next';
import { type ThemeType } from 'react-css-themr';
import { collapsibleListCardFactory } from 'common/components/core/card/CollapsibleListCard';
import { Select } from 'common/components/core/select';
import HandleOutsideClick from 'common/components/core/util/HandleOutsideClick';
import { KeyEventHandler } from 'common/components/core/util/KeyEventsHandler';
import { memoizedImmutable } from 'common/utils/Memoizer';

import {
    Box,
    Stack,
    Text,
    Icon as NeoIcon,
    Columns,
    Column,
    Button
} from '@locus-taxy/neo-ui';
import { type FpPjPlan } from 'common/models/internal/FpPjPlan';
import { type SalesCategory } from 'common/models/internal/SalesCategory';
import { FpBeat } from 'common/models/internal/FpBeat';
import { MAX_DURATION_FREQUENCY_IN_WEEKS } from 'common/entity/outletMaster/tableConfig';

type State = {
    generalFiltersExpanded: boolean,
    selectedFilters: {
        salesman: ?Array<string>,
        zones: ?Array<string>,
        date: ?Date
    }
};

const Days = Object.freeze({
    Monday: 'monday',
    Tuesday: 'tuesday',
    Wednesday: 'wednesday',
    Thursday: 'thursday',
    Friday: 'friday',
    Saturday: 'saturday',
    Sunday: 'sunday'
});

const DayOptions = Object.freeze([
    { label: 'Monday', value: Days.Monday },
    { label: 'Tuesday', value: Days.Tuesday },
    { label: 'Wednesday', value: Days.Wednesday },
    { label: 'Thursday', value: Days.Thursday },
    { label: 'Friday', value: Days.Friday },
    { label: 'Saturday', value: Days.Saturday },
    { label: 'Sunday', value: Days.Sunday }
]);

type GeneralFilterListProps = {
    pjPlan: FpPjPlan,
    outletSalesCategories: Array<SalesCategory>,
    theme: ThemeType,
    selectedSalesman: string,
    onSalesmanChange: () => void,
    onDaySelectionChange: () => void,
    onSalesCategoryChange: () => void,
    onWeekSelectionChange: () => void,
    selectedDays: string,
    selectedWeeks: string,
    selectedSalesCategories: string,
    t: TFunction
};

class GeneralFilterList extends React.PureComponent<GeneralFilterListProps> {
    weekOptions = Object.freeze(
        Array(MAX_DURATION_FREQUENCY_IN_WEEKS)
            .fill(1)
            .map((_, index) => ({
                label: this.props.t('entityManagement:fpPlanView.weekNumber', {
                    count: index + 1
                }),
                value: `week${index + 1}`
            }))
    );

    @memoizedImmutable
    getSalesmanOptions() {
        const { riderIds } = this.props.pjPlan.ridersInfo;
        const riderOptions = riderIds.map(rider => {
            return {
                value: rider.riderId,
                label: rider.riderId
            };
        });
        return riderOptions;
    }

    getSalesCategoryList() {
        const { outletSalesCategories } = this.props;
        if (outletSalesCategories) {
            const salesCategoryList = outletSalesCategories.map(
                salesCategory => {
                    return {
                        value: salesCategory.id,
                        label: salesCategory.name
                    };
                }
            );
            return salesCategoryList;
        }
        return [];
    }

    render() {
        const {
            theme,
            selectedSalesman,
            onSalesmanChange,
            onDaySelectionChange,
            onSalesCategoryChange,
            onWeekSelectionChange,
            selectedDays,
            selectedWeeks,
            selectedSalesCategories,
            t
        } = this.props;
        return (
            <Box>
                <Stack space="medium">
                    <Columns alignY="center">
                        <Column width="fluid">
                            <Select
                                value={selectedSalesman}
                                theme={theme}
                                isMulti
                                onChange={onSalesmanChange}
                                placeholder={t(
                                    'entityManagement:fpPlanView.selectSalesman'
                                )}
                                options={this.getSalesmanOptions()}
                            />
                        </Column>
                    </Columns>
                    <Columns alignY="center">
                        <Column width="fluid">
                            <Select
                                value={selectedSalesCategories}
                                theme={theme}
                                isMulti
                                placeholder={t(
                                    'entityManagement:fpPlanView.selectSalesCategory'
                                )}
                                options={this.getSalesCategoryList()}
                                onChange={onSalesCategoryChange}
                            />
                        </Column>
                    </Columns>
                    <Columns wrap>
                        <Column width="1/2">
                            <Select
                                value={selectedWeeks}
                                theme={theme}
                                isMulti
                                placeholder={t(
                                    'entityManagement:fpPlanView.selectWeek'
                                )}
                                options={this.weekOptions}
                                onChange={onWeekSelectionChange}
                            />
                        </Column>
                        <Column width="1/2">
                            <Select
                                value={selectedDays}
                                theme={theme}
                                isMulti
                                placeholder={t(
                                    'entityManagement:fpPlanView.selectDay'
                                )}
                                options={DayOptions}
                                onChange={onDaySelectionChange}
                            />
                        </Column>
                    </Columns>
                </Stack>
            </Box>
        );
    }
}

const GeneralFilters = collapsibleListCardFactory({
    CollapseComponent: GeneralFilterList
});

type FpBeatFilterProps = {
    selectedFilters: Map<string, Array<string>>,
    t: TFunction,
    getAllFilters: () => void,
    onClearFilter: () => void,
    applyFilters: (
        selectedDays: Array<string>,
        selectedWeeks: Array<string>,
        selectedSalesCategories: Array<string>,
        selectedSalesman: Array<string>
    ) => void,
    beats: FpBeat,
    closeFilterPopup: () => void,
    filterPopupOpen: boolean,
    theme: ThemeType,
    handleClickOutside: () => void
};

class FpBeatFilters extends React.Component<FpBeatFilterProps, State> {
    generalFilterPopupRef: React.RefObject;

    constructor(props) {
        super(props);
        this.state = {
            generalFiltersExpanded: true,
            selectedSalesman: props.selectedFilters.get('salesman') || [],
            selectedDays: props.selectedFilters.get('days') || [],
            selectedWeeks: props.selectedFilters.get('weeks') || [],
            selectedSalesCategories:
                props.selectedFilters.get('salesCategories') || []
        };
    }

    onSalesmanChange = values => {
        this.setState({
            selectedSalesman: values
        });
    };

    onWeekSelectionChange = values => {
        this.setState({
            selectedWeeks: values
        });
    };

    onDaySelectionChange = values => {
        this.setState({
            selectedDays: values
        });
    };

    onSalesCategoryChange = values => {
        this.setState({
            selectedSalesCategories: values
        });
    };

    onGeneralFiltersToggle = () => {
        const { generalFiltersExpanded } = this.state;
        this.setState({
            generalFiltersExpanded: !generalFiltersExpanded
        });
    };

    onApplyFilter = () => {
        const {
            selectedDays,
            selectedWeeks,
            selectedSalesCategories,
            selectedSalesman
        } = this.state;
        const { applyFilters } = this.props;
        applyFilters(
            selectedDays,
            selectedWeeks,
            selectedSalesCategories,
            selectedSalesman
        );
    };

    getPopupRef = ref => {
        if (!ref) {
            return null;
        }
        this.generalFilterPopupRef = ref;
    };

    clearFilter = () => {
        const { onClearFilter } = this.props;
        this.setState(
            {
                selectedSalesman: [],
                selectedDays: [],
                selectedWeeks: [],
                selectedSalesCategories: []
            },
            () => {
                onClearFilter();
            }
        );
    };

    render() {
        const {
            theme,
            onFilterToggle,
            closeFilterPopup,
            pjPlan,
            outletSalesCategories,
            t
        } = this.props;
        const {
            generalFiltersExpanded,
            salesmanOptions,
            selectedSalesman,
            selectedSalesCategories,
            selectedWeeks,
            selectedDays
        } = this.state;
        return (
            <Box onClick={onFilterToggle}>
                <Stack space="xSmall">
                    <Box height="56px" p={4} className={theme.filterHeading}>
                        <Columns alignY="center" height="auto">
                            <Column width="fluid">
                                <Text fontSize={3} fontWeight={600}>
                                    {t('fpPlanView.filters')}
                                </Text>
                            </Column>
                            <Column width="content">
                                <NeoIcon
                                    icon="cross"
                                    size="large"
                                    onClick={closeFilterPopup}
                                />
                            </Column>
                        </Columns>
                    </Box>

                    <Box
                        height="250px"
                        overflowY="scroll"
                        className={theme.generalFiltersWrap}
                    >
                        <GeneralFilters
                            t={t}
                            pjPlan={pjPlan}
                            outletSalesCategories={outletSalesCategories}
                            onSalesmanChange={this.onSalesmanChange}
                            selectedSalesman={selectedSalesman}
                            onWeekSelectionChange={this.onWeekSelectionChange}
                            selectedWeeks={selectedWeeks}
                            onDaySelectionChange={this.onDaySelectionChange}
                            selectedDays={selectedDays}
                            onSalesCategoryChange={this.onSalesCategoryChange}
                            selectedSalesCategories={selectedSalesCategories}
                            salesmanOptions={salesmanOptions}
                            title={t('fpPlanView.generalFilter')}
                            onClick={this.onGeneralFiltersToggle}
                            isExpandable
                            isExpanded={generalFiltersExpanded}
                        />
                    </Box>
                    <Box className={theme.filterFooter} p={2}>
                        <Columns
                            align="right"
                            alignY="center"
                            space="small"
                            wrap
                        >
                            <Column width="content">
                                <Button
                                    palette="primary"
                                    onClick={this.clearFilter}
                                >
                                    {t('fpPlanView.clearAll')}
                                </Button>
                            </Column>
                            <Column width="content">
                                <Button
                                    appearance="solid"
                                    palette="primary"
                                    onClick={this.onApplyFilter}
                                >
                                    {t('fpPlanView.applyFilters')}
                                </Button>
                            </Column>
                        </Columns>
                    </Box>
                </Stack>
            </Box>
        );
    }
}

export default HandleOutsideClick(KeyEventHandler(FpBeatFilters));
